import { useTranslation } from "react-i18next";
import Box from "/app/src/components/generic/components/box";
import { Button, Alert, Divider, Spinner } from "@heroui/react";
import RedButton from "/app/src/components/HeroUi/RedButton";
import { Integration, Order } from "/app/src/models";
import GridWrapper from "/app/src/components/generic/gridWrapper";
import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getProcessingStatusResponse,
  queueOrderResponse,
} from "/app/src/types/processing";
import { processService, orderService } from "/app/src/services";
import TaskProcessor from "./taskProcessor";
import CurrentOrderLine from "./lines";
import { toast } from "sonner";

/**
 * OrderDetails component displays detailed information for a specific order.
 *
 * @param {{ order: Order }} props - The component props.
 * @param {Order} props.order - The order object to display details for.
 * @returns {JSX.Element} The rendered component displaying order details.
 */
export default function ManualOrderDetails({
  order,
  integration,
}: {
  order: Order;
  integration: Integration;
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [disableProcessingStatus, setDisableProcessingStatus] = useState(false);
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [allocating, setAllocating] = useState(false);
  const [deallocating, setDeallocating] = useState(false);
  const [localAllocationStatus, setLocalAllocationStatus] = useState<
    string | undefined
  >(order?.allocationStatus as string | undefined);

  const allocationStatus =
    localAllocationStatus ?? (order?.allocationStatus as string | undefined);
  const isNotAllocated =
    allocationStatus === "Not Allocated" || allocationStatus === "Not Allocate";
  const isAllocatedOrPartial =
    allocationStatus === "Fully Allocated" ||
    allocationStatus === "Partially Allocated";
  const allocateLabel = isAllocatedOrPartial
    ? t("translation:reallocate")
    : t("translation:allocate");

  const orderDetails = useMemo(() => {
    return [
      {
        label: t("translation:allocation_status", "Allocation Status"),
        value: allocationStatus,
      },
      { label: t("translation:order_name"), value: order?.name },
      {
        label: t("translation:direction_type"),
        value: order?.directionType === 1 ? "Put" : "Pick",
      },
      { label: t("translation:deadline"), value: order?.deadline },
      { label: t("translation:info1"), value: order?.info1 },
      { label: t("translation:info2"), value: order?.info2 },
      { label: t("translation:info3"), value: order?.info3 },
      { label: t("translation:info4"), value: order?.info4 },
      { label: t("translation:info5"), value: order?.info5 },
      {
        label: t("translation:order_lines"),
        value: order?.order_lines ? order.order_lines.length : 0,
      },
    ];
  }, [order, t, allocationStatus]);

  const { data: processingResponse, refetch: startProcessing } = useQuery({
    queryKey: ["startProcessing", order.id],
    queryFn: () => {
      return processService.queueOrder(order.id).then((response) => {
        if (!response?.queued) {
          toast.error("Order is unprocessable.");
        }
        return response;
      });
    },
    initialData: {
      queued: false,
      processId: null,
      message: "",
      workflowId: null,
    } as queueOrderResponse,
    enabled: false,
    select: (data) => data,
    onSuccess: (data) => {
      setWorkflowId(data.workflowId);
      setProcessingId(data.processId);
    },
  });

  const { data: workflowStatus, refetch: refetchWorkflowStatus } = useQuery({
    queryKey: ["workflowStatus", workflowId],
    queryFn: () => {
      return processService.getWorkflowStatus(workflowId);
    },
    enabled: Boolean(workflowId),
    cacheTime: 10000,
    refetchInterval: 5000,
    select: (data) => data,
    onSuccess: (data) => {
      setProcessingId((prev) =>
        prev !== data.activeProcessId ? data.activeProcessId : prev,
      );
    },
  });

  const handleStartProcessing = useCallback(() => {
    if (isNotAllocated) {
      toast.error(
        t(
          "translation:order_must_be_allocated_first",
          "Order must be allocated before processing.",
        ),
      );
      return;
    }
    if (order?.incompleteAllocations) {
      toast.error(
        t(
          "translation:order_incomplete_allocations",
          "Order is unable to process due to incomplete allocations.",
        ),
      );
      return;
    }
    setLoading(true);
    startProcessing().then(() => {
      setLoading(false);
    });
  }, [startProcessing, isNotAllocated, t, order?.incompleteAllocations]);

  const onAllocate = useCallback(async () => {
    try {
      setAllocating(true);
      const result = await orderService.allocate(String(order.id));
      if (result?.fullyAllocated) {
        setLocalAllocationStatus("Fully Allocated");
        const hasCount = typeof result?.allocatedLines === "number";
        if (hasCount) {
          toast.success(
            t(
              "translation:order_allocated_success_with_lines",
              "Order allocated: {{count}} lines",
              { count: result.allocatedLines },
            ),
          );
        } else {
          toast.success(
            t("translation:order_allocated_success", "Order allocated"),
          );
        }
      } else {
        const msg =
          result?.message ||
          (result?.status
            ? t(
                "translation:allocation_incomplete_with_status",
                "Allocation incomplete: {{status}}",
                { status: result.status },
              )
            : t(
                "translation:order_allocation_unsuccessful",
                "Allocation unsuccessful",
              ));
        toast.error(msg);
        if (result?.status) setLocalAllocationStatus(result.status);
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        t("translation:order_allocation_failed", "Allocation failed");
      toast.error(msg);
    } finally {
      setAllocating(false);
    }
  }, [order.id, t]);

  const onDeallocate = useCallback(async () => {
    try {
      setDeallocating(true);
      const result = await orderService.deallocate(String(order.id));
      if (result?.deallocated) {
        setLocalAllocationStatus("Not Allocated");
        toast.success(
          result?.message ||
            t("translation:order_deallocated_success", "Order deallocated"),
        );
      } else {
        toast.error(
          result?.message ||
            t(
              "translation:order_deallocation_unsuccessful",
              "Deallocation unsuccessful",
            ),
        );
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        t("translation:order_deallocation_failed", "Deallocation failed");
      toast.error(msg);
    } finally {
      setDeallocating(false);
    }
  }, [order.id, t]);

  const { data: processingStatus, refetch: refetchProcessingStatus } = useQuery(
    {
      queryKey: ["processingStatus", processingId],
      queryFn: () => {
        return processService.getProcessingStatus(processingId);
      },
      initialData: {
        status: "",
        message: "",
        inputNeeded: "",
        active: false,
        locationName: "",
        lot: "",
        serialNumber: "",
        value: null,
        field: "",
        materialName: "",
      } as getProcessingStatusResponse,
      enabled: Boolean(
        workflowStatus?.workflowType !== "FINISHED" &&
        !disableProcessingStatus &&
        processingId,
      ),
      select: (data) => {
        return data;
      },
    },
  );

  return (
    <>
      {workflowStatus?.workflowType === "FINISHED" && (
        <Alert
          className="mb-[20px]"
          color="success"
          title={t("translation:order_finished_processing")}
        />
      )}
      <Box>
        <div className="flex justify-between items-center">
          <h2 className="text-[18px] mb-0">{t("translation:order_details")}</h2>
          <div className="flex space-x-4">
            <Button
              isLoading={loading}
              onPress={handleStartProcessing}
              radius="none"
              color="primary"
              isDisabled={
                workflowStatus?.workflowType === "FINISHED" ||
                processingResponse?.queued
              }
            >
              {t("translation:start_processing")}
            </Button>
            <Button
              isLoading={allocating}
              onPress={onAllocate}
              radius="none"
              color="primary"
              isDisabled={!order?.id}
              variant="bordered"
            >
              {allocateLabel}
            </Button>
            {!isNotAllocated && (
              <RedButton
                isLoading={deallocating}
                onPress={onDeallocate}
                radius="none"
                isDisabled={!order?.id}
                variant="bordered"
              >
                {t("translation:deallocate")}
              </RedButton>
            )}
          </div>
        </div>
        <Divider className="my-3" />
        <GridWrapper responsive={{ sm: 1, md: 2, lg: 3 }}>
          {orderDetails.map(
            (detail) =>
              detail.value && (
                <div
                  key={detail.label}
                  className="min-h-[40px] px-[8px] py-[6px] flex flex-col justify-start"
                >
                  <h3 className="!mb-0 text-[12px]">{detail.label}</h3>
                  <div className="!mb-0 text-[14px]">{detail.value}</div>
                </div>
              ),
          )}
        </GridWrapper>
      </Box>
      {workflowStatus?.workflowType !== "FINISHED" &&
        processingStatus?.active &&
        (processingStatus?.status === "Busy" || loading ? (
          <div className="h-[336px] flex justify-center items-center">
            <Spinner size="lg" />
          </div>
        ) : (
          <Box>
            <CurrentOrderLine
              processingStatus={processingStatus}
              integration={integration}
            />
            <Divider className="my-3" />
            <TaskProcessor
              processingId={processingId}
              setProcessingId={setProcessingId}
              processingStatus={processingStatus}
              onStatusUpdate={refetchProcessingStatus}
              workflowStatus={refetchWorkflowStatus}
              order={order}
              setDisableProcessingStatus={setDisableProcessingStatus}
              integration={integration}
            />
          </Box>
        ))}
    </>
  );
}
